import React, { useContext } from 'react';
import { IconButton, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import MuiDialog from '../../SystemDesign/MuiDialog';
import { useDeleteQuestionOptionMutation } from '../../services/SkillsManagement';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';

/**
 * DeleteOption - confirm and delete a question option
 * Can render as an icon button (inline) or as a menu item
 */
const DeleteOption = ({ option, row, onDeleted, as = 'icon', disabled = false }) => {
  const [deleteOption, { isLoading }] = useDeleteQuestionOptionMutation();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const dispatch = useDispatch();
  const questions = useSelector((state) => state?.quizQuestions || state?.questionQuizReducer || []);

  const o = option || row?.original || row || {};
  const rawId = o?.questionOptionId ?? o?.optionId ?? o?.id;
  const optionId = rawId != null ? Number(rawId) : NaN;
  const labelText = o?.optionText || 'this option';
  // Do not return null when id is missing; allow local removal confirmation

  const handleDelete = async () => {
    try {
      // Debug trace for id resolution
      // eslint-disable-next-line no-console
      console.debug('DeleteOption handleDelete - resolved ids:', { rawId, optionId, option });

      // If no id present at all, treat as local removal only
      if (rawId == null || String(rawId).trim() === '') {
        onDeleted?.({ optionId: rawId });
        setInfoAlert({ severity: 'success', message: 'Option removed' });
        setOpenSnackBar(true);
        return true;
      }

      try {
        // Allow numeric or string id as-is; backend expects path param
        const res = await deleteOption({ id: Number.isNaN(optionId) ? String(rawId) : optionId }).unwrap();
        const successMessage = typeof res === 'string' ? res : (res?.message || 'Option deleted successfully');
        setInfoAlert({ severity: 'success', message: successMessage });
        setOpenSnackBar(true);
        const finalId = Number.isNaN(optionId) ? rawId : optionId;
        onDeleted?.({ optionId: finalId });

        // Update option slice (if used elsewhere)
        dispatch({ type: actions.DELETE_QUESTION_OPTION, optionId: Number(finalId) });

        // Update nested options inside question in questionQuizReducer
        const qid = Number(o?.questionId ?? o?.question?.questionId);
        if (Number.isFinite(qid)) {
          const currentQ = (questions || []).find((q) => Number(q?.questionId ?? q?.id) === qid);
          if (currentQ) {
            const filteredOpts = Array.isArray(currentQ.options)
              ? currentQ.options.filter((opt) => {
                  const oid = opt?.optionId ?? opt?.questionOptionId ?? opt?.id;
                  return Number(oid) !== Number(finalId);
                })
              : [];
            dispatch({ type: actions.UPDATE_QUIZ_QUESTION, payload: { ...currentQ, options: filteredOpts } });
          }
        }
        return true;
      } catch (parseErr) {
        if (parseErr?.originalStatus === 200) {
          const msg = parseErr?.data || 'Option deleted successfully';
          setInfoAlert({ severity: 'success', message: msg });
          setOpenSnackBar(true);
          const finalId = Number.isNaN(optionId) ? rawId : optionId;
          onDeleted?.({ optionId: finalId });
          dispatch({ type: actions.DELETE_QUESTION_OPTION, optionId: Number(finalId) });
          const qid = Number(o?.questionId ?? o?.question?.questionId);
          if (Number.isFinite(qid)) {
            const currentQ = (questions || []).find((q) => Number(q?.questionId ?? q?.id) === qid);
            if (currentQ) {
              const filteredOpts = Array.isArray(currentQ.options)
                ? currentQ.options.filter((opt) => {
                    const oid = opt?.optionId ?? opt?.questionOptionId ?? opt?.id;
                    return Number(oid) !== Number(finalId);
                  })
                : [];
              dispatch({ type: actions.UPDATE_QUIZ_QUESTION, payload: { ...currentQ, options: filteredOpts } });
            }
          }
          return true;
        }
        throw parseErr;
      }
    } catch (error) {
      const errorMessage = error?.data?.message ? String(error.data.message) : (error?.message || 'Failed to delete option');
      // eslint-disable-next-line no-console
      console.error('Delete option error', error);
      setInfoAlert({ severity: 'error', message: errorMessage });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = as === 'menu' ? (
    <MenuItem disabled={disabled}>
      <ListItemIcon>
        <DeleteIcon fontSize="small" color="error" />
      </ListItemIcon>
      <ListItemText>Delete</ListItemText>
    </MenuItem>
  ) : (
    <IconButton size="small" color="error" aria-label="delete option" disabled={disabled}>
      <DeleteOutlineIcon fontSize="small" />
    </IconButton>
  );

  const dialogContent = (
    <div>
      <p>Are you sure you want to delete this option?</p>
      <p><em>{labelText}</em></p>
      <p>This action is irreversible.</p>
    </div>
  );

  return (
    <MuiDialog
      icon={<DeleteOutlineIcon />}
      triggerButton={triggerButton}
      title="Delete"
      subtitle="You are about to delete an option"
      content={dialogContent}
      maxWidth="xs"
      existAction={true}
      actionTitle="Delete"
      actionFunction={handleDelete}
      isLoading={isLoading}
    />
  );
};

DeleteOption.propTypes = {
  option: PropTypes.object,
  row: PropTypes.object,
  onDeleted: PropTypes.func,
  as: PropTypes.oneOf(['icon', 'menu']),
  disabled: PropTypes.bool,
};

export default DeleteOption;

